import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { publicAxiosInstance } from '../../services/AxiosService';
// Import actual logo image
import logoImage from '../../assets/images/logo.jpeg';

// Support categories with descriptions
const SUPPORT_CATEGORIES = [
  { 
    id: 'onboarding',
    label: 'Onboarding Support',
    description: 'Help with setting up your account and getting started'
  },
  { 
    id: 'trial',
    label: 'Request Free Trial',
    description: 'Learn more about our free trial options'
  },
  { 
    id: 'compliance',
    label: 'Compliance Questions',
    description: 'Questions about SOX, SOC, or other compliance frameworks'
  },
  { 
    id: 'technical',
    label: 'Technical Support',
    description: 'Help with technical issues or platform features'
  },
  { 
    id: 'pricing',
    label: 'Pricing & Plans',
    description: 'Information about our pricing and subscription plans'
  }
];

// Create a ref-forwarding component to expose methods
export const ChatSupport = forwardRef((props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'support',
      text: 'Hi there! How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showCategorySelect, setShowCategorySelect] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [emailError, setEmailError] = useState('');
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages when new one is added
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // When first opening the chat, show category selection after a short delay
  useEffect(() => {
    if (isOpen && !userEmail && !showEmailForm && !showCategorySelect && messages.length === 1) {
      // Show category selection after a short delay
      const timer = setTimeout(() => {
        setShowCategorySelect(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, userEmail, showEmailForm, showCategorySelect, messages.length]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  const handleEmailChange = (e) => {
    setUserEmail(e.target.value);
    // Clear error when user is typing
    if (emailError) setEmailError('');
  };

  const handleNameChange = (e) => {
    setUserName(e.target.value);
  };

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const submitUserInfo = (e) => {
    e.preventDefault();
    
    // Validate email
    if (!userEmail || !validateEmail(userEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setShowEmailForm(false);
    
    // Add system message confirming email was captured
    const emailConfirmationMessage = {
      sender: 'support',
      text: `Thanks ${userName || 'there'}! I'll use ${userEmail} to follow up with you. What can I help you with?`,
      timestamp: new Date()
    };
    
    setMessages(prevMessages => [...prevMessages, emailConfirmationMessage]);
  };

  const selectSupportCategory = (category) => {
    setSelectedCategory(category);
    setShowCategorySelect(false);
    
    // Add user message showing category selection
    const userMessage = {
      sender: 'user',
      text: `I need help with: ${category.label}`,
      timestamp: new Date()
    };
    
    // Add response acknowledging selection and asking for contact info
    const responseMessage = {
      sender: 'support',
      text: `Thanks for reaching out about ${category.label}. To better assist you, could you please share your contact information?`,
      timestamp: new Date()
    };
    
    setMessages(prevMessages => [...prevMessages, userMessage, responseMessage]);
    setShowEmailForm(true);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === '') return;

    // Add user message to chat
    const userMessage = {
      sender: 'user',
      text: newMessage,
      timestamp: new Date()
    };
    
    setMessages(prevMessages => [...prevMessages, userMessage]);
    const messageToBeSent = newMessage;
    setNewMessage('');
    setIsSending(true);
    
    // If we don't have user email yet, ask for it
    if (!userEmail) {
      setShowEmailForm(true);
      setIsSending(false);
      return;
    }

    try {
      // Send message to backend using publicAxiosInstance (no auth required)
      await publicAxiosInstance.post('chat-support/', {
        message: messageToBeSent,
        email: userEmail,
        name: userName || 'Website Visitor',
        category: selectedCategory ? selectedCategory.id : 'general'
      });
      
      // Add response message
      const responseMessage = {
        sender: 'support',
        text: "Thanks for reaching out! We've received your message and will get back to you soon at your email address.",
        timestamp: new Date()
      };
      
      setMessages(prevMessages => [...prevMessages, responseMessage]);
      setIsSending(false);
      setIsEmailSent(true);
      
      // After some time, let the user know they can send another message
      setTimeout(() => {
        setIsEmailSent(false);
      }, 10000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage = {
        sender: 'support',
        text: "Sorry, we encountered an error sending your message. Please try again or email us directly.",
        timestamp: new Date()
      };
      
      setMessages(prevMessages => [...prevMessages, errorMessage]);
      setIsSending(false);
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name || name === 'Website Visitor') return 'G';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Expose the openChat method to parent components via ref
  useImperativeHandle(ref, () => ({
    openChat: (category) => {
      setIsOpen(true);
      
      // If a specific category is specified, select it automatically
      if (category && SUPPORT_CATEGORIES.some(c => c.id === category)) {
        const selectedCat = SUPPORT_CATEGORIES.find(c => c.id === category);
        
        // Wait for next tick to ensure the chat is open
        setTimeout(() => {
          setShowCategorySelect(false);
          setSelectedCategory(selectedCat);
          
          // Add user message showing category selection
          const userMessage = {
            sender: 'user',
            text: `I need help with: ${selectedCat.label}`,
            timestamp: new Date()
          };
          
          // Add response acknowledging selection and asking for contact info
          const responseMessage = {
            sender: 'support',
            text: `Thanks for reaching out about ${selectedCat.label}. To better assist you, could you please share your contact information?`,
            timestamp: new Date()
          };
          
          setMessages(prevMessages => [...prevMessages, userMessage, responseMessage]);
          setShowEmailForm(true);
        }, 100);
      }
      // When no category is specified, just open the chat with default behavior
      // The category selection will be shown automatically
    }
  }));

  return (
    <>
      {/* Chat button */}
      <button
        onClick={handleOpen}
        className="fixed bottom-5 right-5 z-40 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all"
        aria-label="Open chat support"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </button>
      
      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-20 right-5 z-40 w-80 sm:w-96 bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 flex justify-between items-center">
            <div className="flex items-center">
              {/* Logo image */}
              <img 
                src={logoImage} 
                alt="AuditAI Logo" 
                className="h-7 w-auto mr-2 rounded" 
              />
              <h3 className="font-medium">AuditAI Support</h3>
            </div>
            <button 
              onClick={handleClose}
              className="text-white hover:text-blue-100"
              aria-label="Close chat"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto max-h-80 bg-slate-50">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`mb-3 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.sender === 'support' && (
                  <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium mr-2 flex-shrink-0 overflow-hidden">
                    <img 
                      src={logoImage} 
                      alt="AuditAI Support" 
                      className="h-full w-full object-cover" 
                    />
                  </div>
                )}
                
                <div 
                  className={`max-w-[75%] rounded-lg px-4 py-2 ${
                    message.sender === 'user' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white border border-slate-200 text-slate-700'
                  }`}
                >
                  <p className="text-sm">{message.text}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                
                {message.sender === 'user' && (
                  <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-sm font-medium ml-2 flex-shrink-0">
                    {getInitials(userName)}
                  </div>
                )}
              </div>
            ))}
            
            {/* Support category selection */}
            {showCategorySelect && (
              <div className="mb-3 flex justify-start">
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium mr-2 flex-shrink-0 overflow-hidden">
                  <img 
                    src={logoImage} 
                    alt="AuditAI Support" 
                    className="h-full w-full object-cover" 
                  />
                </div>
                <div className="max-w-[85%] rounded-lg px-4 py-3 bg-white border border-slate-200 text-slate-700">
                  <p className="text-sm font-medium mb-2">What can we help you with today?</p>
                  <div className="space-y-2">
                    {SUPPORT_CATEGORIES.map(category => (
                      <button
                        key={category.id}
                        onClick={() => selectSupportCategory(category)}
                        className="w-full text-left px-3 py-2 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm transition-colors"
                      >
                        <div className="font-medium">{category.label}</div>
                        <div className="text-xs text-blue-600 mt-0.5">{category.description}</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs mt-2 text-slate-500">Or type your message below to get started with general support.</p>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Email collection form */}
          {showEmailForm && (
            <form onSubmit={submitUserInfo} className="border-t border-slate-200 p-3 bg-blue-50">
              <p className="text-sm text-slate-700 mb-2">
                To better assist you, please provide your contact information:
              </p>
              <div className="space-y-2">
                <input
                  type="text"
                  value={userName}
                  onChange={handleNameChange}
                  placeholder="Your name (optional)"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div>
                  <input
                    type="email"
                    value={userEmail}
                    onChange={handleEmailChange}
                    placeholder="Your email address *"
                    required
                    className={`w-full border ${emailError ? 'border-red-500' : 'border-slate-300'} rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {emailError && (
                    <p className="text-xs text-red-500 mt-1">{emailError}</p>
                  )}
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 rounded-md text-sm font-medium"
                >
                  Continue
                </button>
              </div>
            </form>
          )}
          
          {/* Input */}
          {!showEmailForm && (
            <form onSubmit={sendMessage} className="border-t border-slate-200 p-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={handleInputChange}
                  placeholder="Type your message..."
                  className="flex-1 border border-slate-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSending || isEmailSent}
                />
                <button
                  type="submit"
                  className={`bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2 rounded-full
                    ${(isSending || isEmailSent) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                  disabled={isSending || isEmailSent}
                  aria-label="Send message"
                >
                  {isSending ? (
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
              {isEmailSent && (
                <p className="text-xs text-green-600 mt-2">
                  Message sent! We'll respond to you at {userEmail} shortly.
                </p>
              )}
              <div className="mt-2 text-xs text-center text-slate-500">
                Contact us directly at <a href="mailto:support@audit-ai.net" className="text-blue-600 hover:underline">support@audit-ai.net</a>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
});

// For simpler imports
export default ChatSupport; 